import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useVocabGameSocket } from './useVocabGameSocket';
import { TeamStat, FinalReport } from './vocabGameTypes';
import { playAnswerPing, playWrongPing, playTeamFinished, playGameStart, playVictory } from './soundEffects';

type Phase = 'setup' | 'lobby' | 'playing' | 'finished';

const QUESTION_OPTIONS = [5, 10, 20, 30, 50];

export const VocabGameHostPage: React.FC = () => {
  const { setId } = useParams<{ setId: string }>();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('setup');
  const [roomCode, setRoomCode] = useState('');
  const [setName, setSetName] = useState('');
  const [questionLimit, setQuestionLimit] = useState(20);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [teams, setTeams] = useState<TeamStat[]>([]);
  const [finalResult, setFinalResult] = useState<FinalReport | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  // Flash map: teamId -> 'correct' | 'wrong' (cleared after 700ms)
  const [flashMap, setFlashMap] = useState<Record<string, 'correct' | 'wrong'>>({});
  // Recent activity feed
  const [activity, setActivity] = useState<Array<{ teamName: string; isCorrect: boolean; ts: number }>>([]);

  const { createRoom, startGame, endGame } = useVocabGameSocket({
    onRoomCreated: (data) => {
      setRoomCode(data.code); setTotalQuestions(data.totalQuestions);
      setSetName(data.setName); setPhase('lobby'); setCreating(false);
    },
    onTeamJoined: (data) => setTeams(data.teams),
    onGameStarted: (data) => {
      setTeams(data.teams); setPhase('playing'); playGameStart();
    },
    onStatsUpdate: (data) => {
      // Merge server data intelligently:
      // - Finished teams → use server data (authoritative, has final score)
      // - Still-playing teams → keep local data (accumulated via onAnswerProgress)
      setTeams(prev => {
        const serverMap = new Map<string, any>(data.teams.map((t: any) => [t.socketId, t]));
        // Also add any new team that might not be in prev yet
        const prevIds = new Set(prev.map(t => t.socketId));
        const merged = prev.map(localTeam => {
          const serverTeam = serverMap.get(localTeam.socketId);
          if (!serverTeam) return localTeam;
          // Use server data only for the team that just finished
          if (serverTeam.finished) return serverTeam;
          // Keep local in-progress stats for other teams
          return localTeam;
        });
        // Add any new teams from server not in prev (shouldn't happen mid-game but safety)
        data.teams.forEach((t: any) => {
          if (!prevIds.has(t.socketId)) merged.push(t);
        });
        return merged;
      });
      playTeamFinished();
    },
    onAnswerProgress: (data) => {
      // Flash animation
      setFlashMap(prev => ({ ...prev, [data.teamId]: data.isCorrect ? 'correct' : 'wrong' }));
      setTimeout(() => setFlashMap(prev => { const n = { ...prev }; delete n[data.teamId]; return n; }), 700);
      // Update team stats with server-authoritative values
      setTeams(prev => prev.map(t => t.socketId === data.teamId ? {
        ...t,
        score: data.currentScore ?? t.score,
        correctCount: data.correctCount ?? (t.correctCount + (data.isCorrect ? 1 : 0)),
        wrongCount: data.wrongCount ?? (t.wrongCount + (!data.isCorrect ? 1 : 0)),
        answeredCount: data.answeredCount,
        progressPct: Math.round(data.answeredCount / data.total * 100),
        correctPct: Math.round((data.correctCount ?? (t.correctCount + (data.isCorrect ? 1 : 0))) / data.total * 100),
        wrongPct: Math.round((data.wrongCount ?? (t.wrongCount + (!data.isCorrect ? 1 : 0))) / data.total * 100),
      } : t));
      // Activity feed
      setActivity(prev => [{ teamName: data.teamName, isCorrect: data.isCorrect, ts: Date.now() }, ...prev].slice(0, 8));
      // Sound
      if (data.isCorrect) playAnswerPing(); else playWrongPing();
    },
    onFinalResult: (data) => {
      setFinalResult(data); setPhase('finished'); playVictory();
    },
    onError: (msg) => { setError(msg); setCreating(false); },
  });

  const handleCreateRoom = useCallback(() => {
    const token = localStorage.getItem('accessToken') || '';
    if (!token) { setError('Vui lòng đăng nhập lại'); return; }
    if (!setId) { setError('Không tìm thấy set từ'); return; }
    setCreating(true);
    setError('');
    createRoom({ setId: Number(setId), setName: '', questionLimit, token });
  }, [setId, questionLimit, createRoom]);

  const joinLink = `${window.location.origin}/join?code=${roomCode}`;

  // ── SETUP PHASE ──────────────────────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <Page>
        <SetupCard>
          <GameIcon>🎮</GameIcon>
          <SetupTitle>Tạo phòng Game</SetupTitle>
          <SetupDesc>Học sinh dùng điện thoại vào <strong>/join</strong> để tham gia</SetupDesc>
          <OptionLabel>Số câu hỏi:</OptionLabel>
          <OptionGrid>
            {QUESTION_OPTIONS.map(n => (
              <OptionBtn key={n} $active={questionLimit === n} onClick={() => setQuestionLimit(n)}>
                {n} câu
              </OptionBtn>
            ))}
            <OptionBtn $active={questionLimit === 999} onClick={() => setQuestionLimit(999)}>
              Tất cả
            </OptionBtn>
          </OptionGrid>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <CreateBtn onClick={handleCreateRoom} disabled={creating}>
            {creating ? '⏳ Đang tạo phòng...' : '🚀 Tạo phòng'}
          </CreateBtn>
          <BackLink onClick={() => navigate(-1)}>← Quay lại</BackLink>
        </SetupCard>
      </Page>
    );
  }

  // ── LOBBY PHASE ───────────────────────────────────────────────────────────
  if (phase === 'lobby') {
    return (
      <Page>
        <LobbyLayout>
          <LobbyLeft>
            <RoomCodeBox>
              <RoomCodeLabel>Mã phòng</RoomCodeLabel>
              <RoomCode>{roomCode}</RoomCode>
              <RoomCodeHint>Học sinh vào <strong>{window.location.origin}/join</strong> và nhập mã này</RoomCodeHint>
            </RoomCodeBox>
            <InfoRow>📚 {setName} • {totalQuestions} câu hỏi</InfoRow>
            <CopyBtn onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join?code=${roomCode}`); }}>
              📋 Sao chép link join
            </CopyBtn>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            <StartBtn onClick={() => startGame(roomCode)} disabled={teams.length === 0}>
              {teams.length === 0 ? '⏳ Đang chờ đội tham gia...' : `▶️ Bắt đầu (${teams.length} đội)`}
            </StartBtn>
          </LobbyLeft>
          <LobbyRight>
            <TeamsTitle>Đội đã vào ({teams.length})</TeamsTitle>
            <TeamList>
              {teams.map((t, i) => (
                <TeamItem key={t.socketId} style={{ animationDelay: `${i * 0.05}s` }}>
                  <TeamAvatar $src={(t as any).avatar}>
                    {!(t as any).avatar && t.name[0]?.toUpperCase()}
                    {(t as any).avatar && <img src={(t as any).avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />}
                  </TeamAvatar>
                  <span>{t.name}</span>
                </TeamItem>
              ))}
              {teams.length === 0 && <WaitingHint>Chờ học sinh vào...</WaitingHint>}
            </TeamList>
          </LobbyRight>
        </LobbyLayout>
      </Page>
    );
  }

  // ── PLAYING PHASE ─────────────────────────────────────────────────────────
  if (phase === 'playing') {
    const finishedCount = teams.filter(t => t.finished).length;
    // Sort by score desc — drives animated reorder
    const sortedTeams = [...teams].sort((a, b) => b.score - a.score);
    // Build rank map: socketId -> sorted index (for absolute positioning)
    const rankMap: Record<string, number> = {};
    sortedTeams.forEach((t, i) => { rankMap[t.socketId] = i; });
    const ROW_H = 128; // px: row height + gap — larger for TV display
    return (
      <Page>
        <PlayingHeader>
          <PlayingTitle>🎮 Game đang diễn ra — <span style={{ color: '#a78bfa' }}>{roomCode}</span></PlayingTitle>
          <PlayingMeta>{finishedCount}/{teams.length} đội hoàn thành</PlayingMeta>
          <EndBtn onClick={() => endGame(roomCode)}>⏹ Kết thúc sớm</EndBtn>
        </PlayingHeader>

        {/* Animated live leaderboard — absolute positioning so rows glide up/down */}
        <TeamRowList $height={teams.length * ROW_H}>
          {teams.map((team) => {
            const rank = rankMap[team.socketId] ?? 0;
            const flash = flashMap[team.socketId];
            const av = (team as any).avatar;
            const total = team.total || 1;
            return (
              <TeamRow
                key={team.socketId}
                $finished={team.finished}
                $flash={flash}
                style={{ top: rank * ROW_H }}
              >
                {/* Rank */}
                <RankCol>#{rank + 1}</RankCol>

                {/* Avatar + Name */}
                <TeamInfoCol>
                  <RowAvatar $src={av}>
                    {!av && team.name[0]?.toUpperCase()}
                    {av && <img src={av} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />}
                  </RowAvatar>
                  <div>
                    <RowName>{team.name}</RowName>
                    {team.finished && <DoneTag>✅ Xong</DoneTag>}
                  </div>
                  {flash && <FlashIcon>{flash === 'correct' ? '✅' : '❌'}</FlashIcon>}
                </TeamInfoCol>

                {/* Progress bar */}
                <ProgressCol>
                  <RowProgressWrap>
                    <RowProgressFill $pct={total > 0 ? (team.correctCount / total) * 100 : 0} $color="#22c55e" />
                    <RowProgressFill $pct={total > 0 ? (team.wrongCount / total) * 100 : 0} $color="#ef4444" />
                  </RowProgressWrap>
                  <RowProgressLabels>
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>✓ {team.correctCount} đúng</span>
                    <span style={{ color: '#94a3b8' }}>{team.answeredCount}/{total} câu</span>
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>✗ {team.wrongCount} sai</span>
                  </RowProgressLabels>
                </ProgressCol>

                {/* Score */}
                <ScoreCol>
                  <RowScore>{team.score.toLocaleString()}</RowScore>
                  <RowScoreLabel>points</RowScoreLabel>
                </ScoreCol>
              </TeamRow>
            );
          })}
        </TeamRowList>

        {/* Activity strip */}
        {activity.length > 0 && (
          <ActivityStrip style={{ marginTop: 16 }}>
            <span style={{ color: '#64748b', fontWeight: 700, marginRight: 8, flexShrink: 0 }}>🔴 Live:</span>
            {activity.slice(0, 8).map((a, i) => (
              <ActivityPill key={i} $correct={a.isCorrect}>
                {a.isCorrect ? '✅' : '❌'} {a.teamName}
              </ActivityPill>
            ))}
          </ActivityStrip>
        )}
      </Page>
    );
  }


  // ── FINISHED PHASE ────────────────────────────────────────────────────────
  if (phase === 'finished' && finalResult) {
    const reportTeam = selectedTeam
      ? finalResult.report.find(r => r.name === selectedTeam) ?? null
      : null;

    return (
      <Page>
        <ResultHeader>
          <ResultTitle>🏆 Kết quả cuối cùng</ResultTitle>
          <ResultMeta>{finalResult.setName} • {finalResult.totalQuestions} câu</ResultMeta>
        </ResultHeader>

        {/* Podium top 3 */}
        {finalResult.ranking.length >= 1 && (
          <Podium>
            {finalResult.ranking.slice(0, 3).map((t, i) => (
              <PodiumItem key={t.socketId} $rank={i + 1}>
                <PodiumEmoji>{['🥇', '🥈', '🥉'][i]}</PodiumEmoji>
                <PodiumName>{t.name}</PodiumName>
                <PodiumScore>{t.score.toLocaleString()}</PodiumScore>
                <PodiumCorrect>{t.correctCount}/{finalResult.totalQuestions} đúng</PodiumCorrect>
              </PodiumItem>
            ))}
          </Podium>
        )}

        {/* Full ranking */}
        <RankingTable>
          <thead>
            <tr>
              <Th>#</Th><Th>Đội</Th><Th>Điểm</Th><Th>Đúng</Th><Th>Sai</Th><Th>Báo cáo</Th>
            </tr>
          </thead>
          <tbody>
            {finalResult.ranking.map((t, i) => (
              <tr key={t.socketId} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Td>{i + 1}</Td>
                <Td><strong>{t.name}</strong></Td>
                <Td style={{ color: '#7c3aed', fontWeight: 700 }}>{t.score.toLocaleString()}</Td>
                <Td style={{ color: '#22c55e' }}>✓ {t.correctCount}</Td>
                <Td style={{ color: '#ef4444' }}>✗ {t.wrongCount}</Td>
                <Td>
                  <ViewBtn onClick={() => setSelectedTeam(selectedTeam === t.name ? null : t.name)}>
                    {selectedTeam === t.name ? 'Đóng' : 'Chi tiết'}
                  </ViewBtn>
                </Td>
              </tr>
            ))}
          </tbody>
        </RankingTable>

        {/* Detail report for selected team */}
        {reportTeam && (
          <DetailPanel>
            <DetailTitle>📋 Chi tiết: {reportTeam.name}</DetailTitle>
            {reportTeam.answers.map((a, i) => (
              <DetailRow key={i} $correct={a.isCorrect}>
                <DetailNum>{i + 1}</DetailNum>
                <DetailQ>{a.question}</DetailQ>
                <DetailA>
                  <span style={{ color: a.isCorrect ? '#22c55e' : '#ef4444' }}>
                    {a.isCorrect ? '✓' : '✗'} {a.yourAnswer}
                  </span>
                  {!a.isCorrect && <CorrectHint>→ {a.correctAnswer}</CorrectHint>}
                </DetailA>
                <DetailPts>{a.pointsEarned > 0 ? `+${a.pointsEarned}` : '0'} pts</DetailPts>
                <DetailTime>{(a.timeTakenMs / 1000).toFixed(1)}s</DetailTime>
              </DetailRow>
            ))}
          </DetailPanel>
        )}

        <PlayAgainBtn onClick={() => navigate(-1)}>← Quay lại Set từ</PlayAgainBtn>
      </Page>
    );
  }

  return null;
};

// ── ANIMATIONS ────────────────────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; }`;
const pulse = keyframes`0%,100% { transform: scale(1); } 50% { transform: scale(1.03); }`;
const slideIn = keyframes`from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: none; }`;

// ── STYLED COMPONENTS ────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  color: white;
`;

const SetupCard = styled.div`
  max-width: 500px;
  margin: 60px auto 0;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px;
  padding: 48px 40px;
  text-align: center;
  backdrop-filter: blur(12px);
  animation: ${fadeIn} 0.4s ease;
`;
const GameIcon = styled.div`font-size: 56px; margin-bottom: 12px;`;
const SetupTitle = styled.h1`font-size: 28px; font-weight: 800; margin: 0 0 8px; background: linear-gradient(90deg,#a78bfa,#60a5fa); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`;
const SetupDesc = styled.p`font-size: 14px; color: #94a3b8; margin: 0 0 28px;`;
const OptionLabel = styled.div`font-size: 13px; font-weight: 600; color: #cbd5e1; margin-bottom: 10px; text-align: left;`;
const OptionGrid = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px;`;
const OptionBtn = styled.button<{ $active: boolean }>`
  padding: 8px 18px; border-radius: 20px; border: 2px solid ${p => p.$active ? '#8b5cf6' : 'rgba(255,255,255,0.15)'};
  background: ${p => p.$active ? '#8b5cf6' : 'transparent'}; color: white; font-weight: 600; cursor: pointer; font-size: 13px; transition: all 0.2s;
  &:hover { border-color: #8b5cf6; }
`;
const CreateBtn = styled.button`
  width: 100%; padding: 16px; border-radius: 14px; border: none;
  background: linear-gradient(135deg, #7c3aed, #4f46e5); color: white; font-size: 17px; font-weight: 700;
  cursor: pointer; transition: all 0.25s; margin-bottom: 12px;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(124,58,237,0.4); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const BackLink = styled.button`background: none; border: none; color: #64748b; cursor: pointer; font-size: 14px; &:hover { color: white; }`;
const ErrorMsg = styled.div`background: rgba(239,68,68,0.15); border: 1px solid #ef4444; border-radius: 8px; padding: 10px; font-size: 13px; color: #fca5a5; margin-bottom: 12px;`;

// Lobby
const LobbyLayout = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 32px; max-width: 1400px; margin: 0 auto; animation: ${fadeIn} 0.4s ease;`;
const LobbyLeft = styled.div`background: rgba(255,255,255,0.05); border-radius: 24px; padding: 48px; border: 1px solid rgba(255,255,255,0.1);`;
const LobbyRight = styled.div`background: rgba(255,255,255,0.05); border-radius: 24px; padding: 40px; border: 1px solid rgba(255,255,255,0.1);`;
const RoomCodeBox = styled.div`text-align: center; margin-bottom: 28px;`;
const RoomCodeLabel = styled.div`font-size: 14px; font-weight: 700; color: #94a3b8; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 12px;`;
const RoomCode = styled.div`font-size: 96px; font-weight: 900; letter-spacing: 14px; color: #a78bfa; animation: ${pulse} 2s infinite; line-height: 1;`;
const RoomCodeHint = styled.div`font-size: 15px; color: #64748b; margin-top: 12px;`;
const InfoRow = styled.div`font-size: 15px; color: #94a3b8; margin-bottom: 20px; text-align: center;`;
const CopyBtn = styled.button`width: 100%; padding: 12px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.15); background: transparent; color: #a5b4fc; cursor: pointer; font-size: 14px; font-weight: 600; margin-bottom: 20px; transition: all 0.2s; &:hover { background: rgba(165,180,252,0.1); }`;
const StartBtn = styled.button`
  width: 100%; padding: 20px; border-radius: 16px; border: none;
  background: ${p => (p.disabled ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg,#22c55e,#16a34a)')}; color: white; font-size: 20px; font-weight: 800;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'}; transition: all 0.25s;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(34,197,94,0.4); }
`;
const TeamsTitle = styled.h3`font-size: 16px; font-weight: 700; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; margin: 0 0 20px;`;
const TeamList = styled.div`display: flex; flex-direction: column; gap: 12px; max-height: 60vh; overflow-y: auto;`;
const TeamItem = styled.div`display: flex; align-items: center; gap: 14px; padding: 14px 18px; background: rgba(255,255,255,0.06); border-radius: 12px; animation: ${slideIn} 0.3s ease both; font-size: 18px; font-weight: 700;`;
const TeamAvatar = styled.div<{ $src?: string }>`width: 48px; height: 48px; border-radius: 50%; background: ${p => p.$src ? 'transparent' : 'linear-gradient(135deg,#8b5cf6,#3b82f6)'}; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 18px; flex-shrink: 0; overflow: hidden;`;
const WaitingHint = styled.div`color: #475569; font-size: 16px; text-align: center; padding: 28px 0;`;

// Playing
const flashGreen = keyframes`0%{box-shadow:0 0 0 0 rgba(34,197,94,0.8)}70%{box-shadow:0 0 0 12px rgba(34,197,94,0)}100%{box-shadow:0 0 0 0 rgba(34,197,94,0)}`;
const flashRed = keyframes`0%{box-shadow:0 0 0 0 rgba(239,68,68,0.8)}70%{box-shadow:0 0 0 12px rgba(239,68,68,0)}100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}`;
const PlayingHeader = styled.div`display: flex; align-items: center; gap: 16px; margin-bottom: 24px; flex-wrap: wrap;`;
const PlayingTitle = styled.h1`font-size: 22px; font-weight: 800; margin: 0; flex: 1;`;
const PlayingMeta = styled.div`font-size: 14px; color: #94a3b8;`;
const EndBtn = styled.button`padding: 10px 20px; border-radius: 10px; border: 1px solid #ef4444; background: rgba(239,68,68,0.15); color: #fca5a5; font-weight: 600; cursor: pointer; font-size: 13px; transition: all 0.2s; &:hover { background: rgba(239,68,68,0.3); }`;
const PlayingLayout = styled.div`display: grid; grid-template-columns: 1fr 280px; gap: 20px; align-items: start;`;
const TeamsGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 14px;`;
const TeamProgressCard = styled.div<{ $finished: boolean; $flash?: 'correct' | 'wrong' }>`
  background: rgba(255,255,255,0.05); border-radius: 16px; padding: 16px; position: relative;
  border: 1px solid ${p => p.$flash === 'correct' ? 'rgba(34,197,94,0.7)' : p.$flash === 'wrong' ? 'rgba(239,68,68,0.7)' : p.$finished ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)'};
  animation: ${p => p.$flash === 'correct' ? css`${flashGreen} 0.7s ease` : p.$flash === 'wrong' ? css`${flashRed} 0.7s ease` : 'none'};
  transition: border-color 0.2s;
`;
const TeamHeaderRow = styled.div`display: flex; align-items: center; gap: 10px; margin-bottom: 10px;`;
const BigAvatar = styled.div<{ $src?: string }>`width: 40px; height: 40px; border-radius: 50%; background: ${p => p.$src ? 'transparent' : 'linear-gradient(135deg,#8b5cf6,#3b82f6)'}; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; flex-shrink: 0; overflow: hidden;`;
const FlashIcon = styled.div`margin-left: auto; font-size: 22px; animation: ${fadeIn} 0.2s ease;`;
const TeamRank = styled.div`font-size: 11px; font-weight: 700; color: #64748b; letter-spacing: 1px; margin-bottom: 4px;`;
const TeamName = styled.div`font-size: 15px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const TeamScore = styled.div`font-size: 17px; font-weight: 800; color: #a78bfa;`;
const ProgressBarWrap = styled.div`display: flex; height: 10px; border-radius: 5px; overflow: hidden; background: rgba(255,255,255,0.1); margin-bottom: 6px;`;
const ProgressBarFill = styled.div<{ $pct: number; $color: string }>`width: ${p => p.$pct}%; background: ${p => p.$color}; transition: width 0.5s ease;`;
const ProgressStats = styled.div`display: flex; justify-content: space-between; font-size: 12px; font-weight: 600;`;
const FinishedBadge = styled.div`position: absolute; top: 8px; right: 8px; background: rgba(34,197,94,0.2); border: 1px solid rgba(34,197,94,0.5); border-radius: 20px; padding: 2px 8px; font-size: 11px; color: #86efac;`;
const ActivityFeed = styled.div`background: rgba(255,255,255,0.04); border-radius: 14px; padding: 16px; border: 1px solid rgba(255,255,255,0.08); position: sticky; top: 20px;`;
const ActivityTitle = styled.div`font-size: 12px; font-weight: 700; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px; display: flex; align-items: center; gap: 6px;`;
const ActivityHint = styled.div`color: #475569; font-size: 13px; text-align: center; padding: 12px 0;`;
const ActivityRow = styled.div<{ $correct: boolean }>`display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; margin-bottom: 6px; font-size: 13px; background: ${p => p.$correct ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'}; border: 1px solid ${p => p.$correct ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}; animation: ${slideIn} 0.3s ease;`;

// Results
const ResultHeader = styled.div`text-align: center; margin-bottom: 28px; animation: ${fadeIn} 0.4s ease;`;
const ResultTitle = styled.h1`font-size: 32px; font-weight: 900; margin: 0 0 4px; background: linear-gradient(90deg,#fbbf24,#f59e0b); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`;
const ResultMeta = styled.div`font-size: 14px; color: #94a3b8;`;
const Podium = styled.div`display: flex; justify-content: center; gap: 16px; margin-bottom: 32px; align-items: flex-end; flex-wrap: wrap;`;
const PodiumItem = styled.div<{ $rank: number }>`
  background: ${p => p.$rank === 1 ? 'linear-gradient(135deg,#f59e0b,#d97706)' : p.$rank === 2 ? 'linear-gradient(135deg,#94a3b8,#64748b)' : 'linear-gradient(135deg,#b45309,#92400e)'};
  border-radius: 16px; padding: 20px 24px; text-align: center; min-width: 140px;
  order: ${p => p.$rank === 1 ? 0 : p.$rank === 2 ? -1 : 1};
  transform: ${p => p.$rank === 1 ? 'scale(1.05)' : 'scale(0.95)'};
`;
const PodiumEmoji = styled.div`font-size: 36px;`;
const PodiumName = styled.div`font-weight: 800; font-size: 14px; margin: 4px 0;`;
const PodiumScore = styled.div`font-size: 20px; font-weight: 900;`;
const PodiumCorrect = styled.div`font-size: 11px; opacity: 0.8; margin-top: 2px;`;
const RankingTable = styled.table`width: 100%; max-width: 900px; margin: 0 auto 24px; border-collapse: collapse; background: rgba(255,255,255,0.04); border-radius: 12px; overflow: hidden; border: 1px solid rgba(255,255,255,0.1);`;
const Th = styled.th`padding: 14px 18px; text-align: left; font-size: 13px; font-weight: 700; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; background: rgba(255,255,255,0.05);`;
const Td = styled.td`padding: 14px 18px; font-size: 15px; color: #f1f5f9; font-weight: 500;`;
const ViewBtn = styled.button`padding: 4px 12px; border-radius: 6px; border: 1px solid rgba(165,180,252,0.4); background: rgba(165,180,252,0.1); color: #a5b4fc; cursor: pointer; font-size: 12px; font-weight: 600; transition: all 0.2s; &:hover { background: rgba(165,180,252,0.2); }`;
const DetailPanel = styled.div`max-width: 800px; margin: 0 auto 24px; background: rgba(255,255,255,0.04); border-radius: 16px; padding: 24px; border: 1px solid rgba(255,255,255,0.1); animation: ${fadeIn} 0.3s ease;`;
const DetailTitle = styled.h3`font-size: 16px; font-weight: 700; margin: 0 0 16px; color: #a5b4fc;`;
const DetailRow = styled.div<{ $correct: boolean }>`display: grid; grid-template-columns: 30px 1fr 1fr 60px 50px; gap: 8px; align-items: center; padding: 10px; border-radius: 8px; background: ${p => p.$correct ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'}; margin-bottom: 6px; font-size: 13px;`;
const DetailNum = styled.div`font-weight: 700; color: #64748b;`;
const DetailQ = styled.div`font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`;
const DetailA = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const CorrectHint = styled.div`font-size: 11px; color: #22c55e;`;
const DetailPts = styled.div`font-weight: 700; color: #a78bfa; font-size: 12px;`;
const DetailTime = styled.div`color: #64748b; font-size: 12px;`;
const PlayAgainBtn = styled.button`display: block; margin: 0 auto; padding: 14px 32px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.2); background: transparent; color: white; font-weight: 700; cursor: pointer; font-size: 15px; transition: all 0.2s; &:hover { background: rgba(255,255,255,0.08); }`;

// ── TV ROW LAYOUT (playing phase) ─────────────────────────────────────────────
const TeamRowList = styled.div<{ $height: number }>`
  position: relative;
  height: ${p => p.$height}px;
  max-width: 1200px;
  margin: 0 auto;
`;

const TeamRow = styled.div<{ $finished: boolean; $flash?: 'correct' | 'wrong' }>`
  position: absolute;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: 64px 220px 1fr 160px;
  align-items: center;
  gap: 20px;
  padding: 18px 24px;
  border-radius: 18px;
  background: rgba(255,255,255,0.06);
  border: 2px solid ${p =>
    p.$flash === 'correct' ? 'rgba(34,197,94,0.8)' :
      p.$flash === 'wrong' ? 'rgba(239,68,68,0.8)' :
        p.$finished ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)'};
  animation: ${p =>
    p.$flash === 'correct' ? css`${flashGreen} 0.7s ease` :
      p.$flash === 'wrong' ? css`${flashRed} 0.7s ease` : 'none'};
  /* Smooth glide when rank changes */
  transition: top 0.6s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s;
`;

const RankCol = styled.div`
  font-size: 36px;
  font-weight: 900;
  color: #64748b;
  text-align: center;
`;

const TeamInfoCol = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const RowAvatar = styled.div<{ $src?: string }>`
  width: 64px; height: 64px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$src ? 'transparent' : 'linear-gradient(135deg,#8b5cf6,#3b82f6)'};
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 24px; color: white; overflow: hidden;
  border: 2px solid rgba(255,255,255,0.15);
`;

const RowName = styled.div`
  font-size: 26px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DoneTag = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #86efac;
  margin-top: 2px;
`;

const ProgressCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RowProgressWrap = styled.div`
  display: flex;
  height: 28px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(255,255,255,0.08);
`;

const RowProgressFill = styled.div<{ $pct: number; $color: string }>`
  width: ${p => p.$pct}%;
  background: ${p => p.$color};
  transition: width 0.5s ease;
  min-width: ${p => p.$pct > 0 ? '6px' : '0'};
`;

const RowProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 700;
`;

const ScoreCol = styled.div`
  text-align: center;
`;

const RowScore = styled.div`
  font-size: 42px;
  font-weight: 900;
  background: linear-gradient(90deg, #a78bfa, #60a5fa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
`;

const RowScoreLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 2px;
`;

const ActivityStrip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  margin-top: 16px;
  background: rgba(255,255,255,0.03);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.07);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const ActivityPill = styled.div<{ $correct: boolean }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: ${p => p.$correct ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'};
  border: 1px solid ${p => p.$correct ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'};
  color: ${p => p.$correct ? '#86efac' : '#fca5a5'};
  animation: ${slideIn} 0.3s ease;
`;

export default VocabGameHostPage;

